import {
  PAYER_TYPE,
} from 'config/constants'
import {
  formatHSCode,
} from 'utils/helpers'

import {
  aggregateCustomsFromDeclarations,
  getSummaryProductPurchaseTotal,
} from './calculation-data-utils'
import {
  formatRub,
  formatUsd,
} from './calculation-money'
import DeliveryRouteLine from './components/delivery-route-line'

export const getCalculationExtraInfo = ({
  calculation,
  resolveProductCode,
  resolveProductCodeValue,
  translate: t,
}: CalculationExtraInfoParams): SummaryCalculatorExtraInfo => {
  const {
    order,
    invoices,
    summaryInvoices,
    packagingSummary,
    summaryInfo,
    route,
    declarations,
    certification,
  } = calculation
  const invoiceProductsById = new Map<number, ProductParamsOutput[]>()

  invoices?.forEach((invoice) => {
    if (invoice.id != null && invoice.products?.length) {
      invoiceProductsById.set(invoice.id, invoice.products)
    }
  })

  const packaging = (summaryInvoices ?? []).flatMap((invoice) => (
    (invoice.products ?? []).map((product) => {
      const hsCode = resolveProductCode?.({ invoiceId: invoice.invoice_id, product, invoiceProductsById }) ?? product.code
      const purchaseTotal = getSummaryProductPurchaseTotal({
        product,
        invoiceProductsById,
        invoiceId: invoice.invoice_id,
      })

      return [
        { label: t('common__text__products'), value: product.name || product.code },
        { label: t('common__text__codeOfHS'), value: resolveProductCodeValue?.({ invoiceId: invoice.invoice_id, product, invoiceProductsById }) ?? (formatHSCode(hsCode) || '—') },
        { label: t('common__text__quantity'), value: product.pieces_total ?? '—' },
        { label: t('common__text__cost'), value: formatUsd({ value: purchaseTotal, fractionDigits: 2 }) },
      ]
    })
  ))
  const packagingTotal = packagingSummary?.price_total ?? summaryInfo?.packaging?.price_total
  const canonicalPackaging = packagingSummary ?? summaryInfo?.packaging
  const canonicalSummary = canonicalPackaging ? { ...canonicalPackaging, ...summaryInfo?.summary } : undefined
  const hasCanonicalPackagingTotal = Boolean(invoices && canonicalSummary?.price_total)
  const isSamePackagingTotal = Number(packagingTotal) === Number(canonicalSummary?.price_total)

  const hasPackagingTotal = packagingTotal != null
  const shouldAddPackagingTotal = !hasCanonicalPackagingTotal || !isSamePackagingTotal

  if (hasPackagingTotal && shouldAddPackagingTotal) {
    packaging.push([
      {
        label: t('common__text__inTotal'),
        value: formatUsd({ value: packagingTotal, fractionDigits: 2 }),
      },
    ])
  }

  const customsTotals = aggregateCustomsFromDeclarations(declarations)
  const canonicalCustoms = summaryInfo?.customs ?? order?.summaryCustoms
  const hasCanonicalCustomsTotal = Boolean(canonicalCustoms && declarations)
  const isSameCustomsTotal = Number(canonicalCustoms?.fees_total) === customsTotals?.total
  const shouldShowCustomsTotal = !hasCanonicalCustomsTotal || !isSameCustomsTotal
  const customs = customsTotals ? [ [
    { label: t('calc__text__customs__customsCost'), value: formatUsd({ value: customsTotals.customsValue }) },
    { label: t('common__text__customsPayments'), value: formatUsd({ value: customsTotals.customsPayments }) },
    { label: t('calc__text__customs__vatAmount'), value: formatUsd({ value: customsTotals.vat }) },
    { label: t('calc__text__customs__excise'), value: formatUsd({ value: customsTotals.excise }) },
    { label: t('calc__text__customs__utilizationFee'), value: formatUsd({ value: customsTotals.utilization }) },
    { label: t('calc__text__customs__environmentalFee'), value: formatUsd({ value: customsTotals.environmental }) },
    { label: t('calc__text__customs__customsDutyCapitalLetter'), value: formatUsd({ value: customsTotals.customsFee }) },
    ...(shouldShowCustomsTotal ? [ {
      label: t('common__text__inTotal'),
      value: formatUsd({ value: customsTotals.total, fractionDigits: 2 }),
    } ] : []),
  ] ] : []

  const orderSummary = summaryInfo?.summary
  const rubExchange = orderSummary?.total_amount_exchange?.RUB
  const total: SummaryCalculatorInfoRow[] = []

  if (orderSummary) {
    total.push([
      {
        label: t('applications__text__calcSummaryTotalUsdAllIn'),
        value: formatUsd({ value: orderSummary.total_amount, fractionDigits: 2 }),
      },
      ...(orderSummary.net_total_amount != null && orderSummary.net_total_amount !== orderSummary.total_amount ? [ {
        label: t('applications__text__calcSummaryTotalUsdExclVat'),
        value: formatUsd({ value: orderSummary.net_total_amount, fractionDigits: 2 }),
      } ] : []),
      ...(orderSummary.vat_total != null && Number(orderSummary.vat_total) !== 0 ? [ {
        label: t('calc__text__customs__vatAmount'),
        value: formatUsd({ value: orderSummary.vat_total, fractionDigits: 2 }),
      } ] : []),
      ...(rubExchange ? [
        {
          label: t('applications__text__calcSummaryRubExclVat'),
          value: `≈ ${formatRub({ value: rubExchange.net_value })}`,
        },
        {
          label: t('applications__text__calcSummaryRubInclVat'),
          value: `≈ ${formatRub({ value: rubExchange.value })}`,
        },
      ] : []),
    ])
  }

  const delivery = route ?? (
    summaryInfo?.delivery && 'freights' in summaryInfo.delivery ? summaryInfo.delivery : undefined
  )
  const hasKnownPayerType = order?.payer_type === PAYER_TYPE.INDIVIDUAL
    || order?.payer_type === PAYER_TYPE.LEGAL_ENTITY
  const hasDeliveryLocation = Boolean(delivery?.location_from?.name || delivery?.location_to?.name)
  const hasDeliveryRoute = Boolean(delivery?.freights?.length) && hasDeliveryLocation

  return {
    main: hasKnownPayerType ? [ [ {
      label: t('calc__text__main__payerType'),
      value: order?.payer_type === PAYER_TYPE.INDIVIDUAL
        ? t('calc__text__main__individual')
        : t('calc__text__main__legalEntity'),
    } ] ] : [],
    packaging,
    delivery: hasDeliveryRoute ? [ [ {
      label: t('applications__text__route'),
      value: <DeliveryRouteLine route={delivery} />,
    } ] ] : [],
    customs,
    certification: certification?.filter((item) => item.has_certificate !== true && item.has_certificate !== null).map((item) => [
      {
        label: `${item.code} (${item.country_code})\n${t('dictionaries__text__certification__typeOfDocument')}`,
        value: item.documents?.[0]?.document_name || '—',
      },
      {
        label: `${item.code} (${item.country_code})\n${t('dictionaries__text__certification__techRegulations')}`,
        value: item.tech_reglament || item.documents?.[0]?.tech_reglament || '—',
      },
    ]),
    total,
  }
}
